import { useState, useEffect } from "react";
import {
  FiEdit2,
  FiFilter,
  FiPlus,
  FiPlusCircle,
  FiRotateCcw,
  FiSearch,
} from "react-icons/fi";
import { useCommercialInteriorDesign } from "../../useHook";
import Swal from "sweetalert2";
import { useNavigate, useSearchParams } from "react-router-dom";
import GlobalPagination from "../../../../Components/Pagination/GlobalPagination";
import AddPopularCommercialInteriorDesign from "../../../../Components/Modals/PopularCommercialInteriorDesign/PopularCommercialInteriorDesign";
import SimilarItemsModal from "../../../../Components/Modals/SimilarItems/SimilarItems";
import RecordPosition from "../../../../Components/Modals/RecordPosition/RecordPosition";
import { MdDelete } from "react-icons/md";
import { IoEye } from "react-icons/io5";
import {
  usePermission,
  SystemPermissions,
} from "../../../../Utils/permissionAccess";

const CommercialInteriorDesignList = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const canCreate = hasPermission(
    SystemPermissions.CommercialInteriorDesign.CREATE,
  );
  const canAddPopular = hasPermission(
    SystemPermissions.CommercialInteriorDesign.ADD_POPULAR_DESIGN,
  );
  const canPosition = hasPermission(
    SystemPermissions.CommercialInteriorDesign.POSITION,
  );
  const canAddSimilar = hasPermission(
    SystemPermissions.CommercialInteriorDesign.ADD_SIMILAR,
  );
  const canView = hasPermission(SystemPermissions.CommercialInteriorDesign.VIEW);
  const canUpdate = hasPermission(
    SystemPermissions.CommercialInteriorDesign.UPDATE,
  );
  const canDelete = hasPermission(
    SystemPermissions.CommercialInteriorDesign.DELETE,
  );
  const canShowActions = canAddSimilar || canUpdate || canView || canDelete;

  const {
    getCommercialInteriorDesign,
    deleteCommercialInteriorDesign,
    updateCommercialInteriorDesignPosition,
  } = useCommercialInteriorDesign();

  const [commercialInteriorDesignData, setCommercialInteriorDesignData] =
    useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [searchFilter, setSearchFilter] = useState("");
  const pageParam = parseInt(searchParams.get("page") || "0", 10);
  const offset = pageParam * limit;
  const [showPopularDesignModal, setShowPopularDesignModal] = useState(false);
  const [isSimilarModalOpen, setIsSimilarModalOpen] = useState(false);
  const [selectedDesignForSimilar, setSelectedDesignForSimilar] = useState(null);
  const [isPositionModalOpen, setIsPositionModalOpen] = useState(false);
  const [selectedDesignForPosition, setSelectedDesignForPosition] =
    useState(null);

  useEffect(() => {
    loadCommercialInteriorDesigns(offset, limit);
  }, [offset]);

  const handlePageChange = (newOffset) => {
    const newPage = Math.floor(newOffset / limit);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("page", String(newPage));
      return next;
    });
  };

  const loadCommercialInteriorDesigns = async (
    currentOffset = offset,
    currentLimit = limit,
    currentSearchFilter = searchFilter,
  ) => {
    setIsLoading(true);
    try {
      await getCommercialInteriorDesign(
        setCommercialInteriorDesignData,
        setTotal,
        currentOffset,
        currentLimit,
        currentSearchFilter,
      );
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load commercial interior designs. Please refresh the page.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async () => {
    setIsLoading(true);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("page", "0");
      return next;
    });
    try {
      await getCommercialInteriorDesign(
        setCommercialInteriorDesignData,
        setTotal,
        0,
        limit,
        searchFilter,
      );
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to search commercial interior designs. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setSearchFilter("");
    setSearchParams({ page: "0" });
    loadCommercialInteriorDesigns(0, limit, "");
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
      reverseButtons: true,
    });
    if (result.isConfirmed) {
      try {
        await deleteCommercialInteriorDesign(id);
        await loadCommercialInteriorDesigns(offset, limit);
        Swal.fire("Deleted!", "Interior design deleted successfully.", "success");
      } catch (error) {
        console.error("Failed to delete commercial interior design:", error);
        Swal.fire(
          "Error!",
          "Failed to delete commercial interior design. Please try again.",
          "error",
        );
      }
    }
  };

  const handlePositionClick = (design) => {
    setSelectedDesignForPosition(design);
    setIsPositionModalOpen(true);
  };

  const handleUpdatePosition = async (commercialInteriorDesignId, newPosition) => {
    await updateCommercialInteriorDesignPosition(
      commercialInteriorDesignId,
      newPosition,
    );
    await loadCommercialInteriorDesigns(offset, limit, searchFilter);
  };

  return (
    <div>
      <div className="flex flex-wrap gap-4 justify-between items-center">
        <h3 className="text-xl font-bold">Commercial Interior Design</h3>
        <div className="flex flex-wrap gap-2">
          {canCreate && (
            <button
              onClick={() => navigate("/commercial-interior-design/create")}
              className="btn btn-primary h-[44px]"
            >
              <FiPlus className="text-white" />
              Add Commercial Interior Design
            </button>
          )}
          {canAddPopular && (
            <button
              onClick={() => setShowPopularDesignModal(true)}
              className="btn btn-primary h-[44px]"
            >
              <FiPlus className="text-white" />
              Add Popular Design
            </button>
          )}
        </div>
      </div>

      <div className="bg-white p-5 w-full rounded-lg pagination-shadow flex flex-wrap xlCustom:flex-nowrap md:justify-end items-center gap-3 my-5">
        <div className="search-wrapper">
          <input
            type="text"
            className="search-input"
            placeholder="Search by name"
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
          />
          <FiSearch className="search-icon" />
        </div>
        <div className="flex flex-wrap md:flex-nowrap gap-2">
          <button className="btn btn-filter filter-btn" onClick={handleSearch}>
            <FiFilter className="text-white" /> Apply Filter
          </button>
          <button
            className="btn btn-reset-filter filter-btn"
            onClick={handleReset}
          >
            <FiRotateCcw className="text-white" /> Reset Filter
          </button>
        </div>
      </div>

      <div className="bg-white w-full rounded-lg pagination-shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full table-auto bg-white rounded-lg overflow-hidden">
            <thead>
              <tr className="brand-gradient text-white">
                <th className="table-th">Sr. No.</th>
                <th className="table-th">Title</th>
                <th className="table-th">Room Size</th>
                {canShowActions && <th className="table-th">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {commercialInteriorDesignData.length > 0 ? (
                commercialInteriorDesignData.map((design, index) => (
                  <tr
                    key={design.id}
                    className={`${
                      index % 2 === 0 ? "bg-gray-50" : "bg-gray-100"
                    } hover:bg-gray-200 transition-colors duration-300`}
                  >
                    <td className="table-td">
                      {canPosition ? (
                        <button
                          type="button"
                          onClick={() => handlePositionClick(design)}
                          className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer font-medium"
                        >
                          {design.position ?? offset + index + 1}
                        </button>
                      ) : (
                        <span className="font-medium text-gray-800">
                          {design.position ?? offset + index + 1}
                        </span>
                      )}
                    </td>
                    <td className="table-td">{design.title}</td>
                    <td className="table-td">{design.room_size}</td>
                    {canShowActions && (
                      <td className="table-td">
                        <div className="flex space-x-2">
                          {canAddSimilar && (
                            <button
                              className="btn btn-action"
                              onClick={() => {
                                setSelectedDesignForSimilar(design.id);
                                setIsSimilarModalOpen(true);
                              }}
                            >
                              <FiPlusCircle className="text-white" />
                              Similar Items
                            </button>
                          )}
                          {canUpdate && (
                            <button
                              className="btn btn-edit text-white"
                              onClick={() =>
                                navigate(
                                  `/commercial-interior-design/edit/${design.id}`,
                                )
                              }
                            >
                              <FiEdit2 className="text-white" /> Edit
                            </button>
                          )}
                          {canView && (
                            <button
                              className="btn btn-view"
                              onClick={() =>
                                navigate(`/commercial-interior-design/${design.id}`)
                              }
                            >
                              <IoEye className="text-white" /> View
                            </button>
                          )}
                          {canDelete && (
                            <button
                              className="btn btn-delete text-white"
                              onClick={() => handleDelete(design.id)}
                            >
                              <MdDelete className="text-white" /> Delete
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={canShowActions ? 4 : 3}
                    className="py-4 px-4 text-center text-gray-500 font-bold"
                  >
                    {isLoading
                      ? "Loading commercial interior designs..."
                      : "No commercial interior designs found"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {commercialInteriorDesignData.length > 0 && total > limit && (
        <div className="mt-4">
          <GlobalPagination
            offset={offset}
            limit={limit}
            total={total}
            onPageChange={handlePageChange}
            className="bg-white"
          />
        </div>
      )}

      {showPopularDesignModal && (
        <AddPopularCommercialInteriorDesign
          isOpen={showPopularDesignModal}
          onClose={() => setShowPopularDesignModal(false)}
        />
      )}

      {isSimilarModalOpen && (
        <SimilarItemsModal
          isOpen={isSimilarModalOpen}
          onClose={() => {
            setIsSimilarModalOpen(false);
            setSelectedDesignForSimilar(null);
          }}
          sourceId={selectedDesignForSimilar}
          sourceType="commercial_interior_design"
        />
      )}

      {isPositionModalOpen && selectedDesignForPosition && (
        <RecordPosition
          isOpen={isPositionModalOpen}
          onClose={() => {
            setIsPositionModalOpen(false);
            setSelectedDesignForPosition(null);
          }}
          currentPosition={selectedDesignForPosition.position ?? 0}
          recordId={selectedDesignForPosition.id}
          onUpdatePosition={handleUpdatePosition}
          moduleName="Commercial Interior Design"
        />
      )}
    </div>
  );
};

export default CommercialInteriorDesignList;
